import { sha256Hex } from "../integration/local-ifc";

export const D2_PROPERTY_PROPOSAL_CANDIDATE_VERSION = "workbench-private-property-proposal-candidate/1" as const;
export const D2_PROPERTY_EXPECTED_RESULT_CANDIDATE_VERSION = "workbench-private-property-expected-result-candidate/1" as const;
export const D2_PROPERTY_CANDIDATE_CAP = 1 as const;

export interface CandidateRecordAssertion {
  readonly entity: string;
  readonly expressId: number;
  readonly sourceRecordSha256: string;
}

export interface CandidatePropertyTargetAssertions {
  readonly occurrence: CandidateRecordAssertion & { readonly globalId: string; readonly name: string };
  readonly propertySet: CandidateRecordAssertion & { readonly name: string };
  readonly property: CandidateRecordAssertion & { readonly name: string };
  readonly relation: CandidateRecordAssertion;
}

export interface DiscoveredCandidatePropertyTarget extends CandidatePropertyTargetAssertions {
  readonly before: CandidateTypedValue;
}

export interface CandidateTypedValue {
  readonly primitiveType: "string" | "number" | "boolean";
  readonly ifcType: string;
  readonly value: string | number | boolean;
}

export interface D2PropertyCandidateAuthoringInput {
  readonly sourceBytes: Uint8Array;
  readonly sourceFilename: string;
  readonly claimedSourceSha256: string;
  readonly target: CandidatePropertyTargetAssertions;
  readonly before: CandidateTypedValue;
  readonly after: CandidateTypedValue;
  readonly preservedUnknownPlanKeys: readonly string[];
}

export interface D2PropertyProposalCandidate {
  readonly contractVersion: typeof D2_PROPERTY_PROPOSAL_CANDIDATE_VERSION;
  readonly status: "private-test-only-candidate";
  readonly authority: "none";
  readonly canonicalBytes: {
    readonly mediaType: "application/json";
    readonly encoding: "UTF-8";
    readonly objectKeys: "lexicographic";
    readonly arrays: "preserve-contract-order";
    readonly insignificantWhitespace: "none";
    readonly bom: false;
    readonly trailingNewline: false;
  };
  readonly source: {
    readonly filename: string;
    readonly byteLength: number;
    readonly sha256: string;
    readonly authorityClaim: false;
  };
  readonly validation: { readonly state: "partial"; readonly serverVerdict: false };
  readonly selectedPopulation: {
    readonly selected: 1;
    readonly proposed: 1;
    readonly alreadySatisfied: 0;
    readonly refused: 0;
    readonly unsupported: 0;
    readonly exactClosure: true;
    readonly candidateCap: typeof D2_PROPERTY_CANDIDATE_CAP;
    readonly candidateCapIsProductionLimit: false;
  };
  readonly operations: readonly [D2PropertyProposalOperation];
  readonly preservationAssertions: {
    readonly unknownPlanKeys: readonly string[];
    readonly untouchedSourceRecords: true;
  };
}

export interface D2PropertyProposalOperation {
  readonly opId: string;
  readonly order: 0;
  readonly kind: "replace-existing-occurrence-property-single-value";
  readonly target: CandidatePropertyTargetAssertions;
  readonly before: CandidateTypedValue;
  readonly after: CandidateTypedValue;
  readonly targetSourceRecordSha256: string;
}

export interface CandidatePropertyProposalOperation {
  readonly opId: string;
  readonly order: number;
  readonly kind: D2PropertyProposalOperation["kind"];
  readonly target: CandidatePropertyTargetAssertions;
  readonly before: CandidateTypedValue;
  readonly after: CandidateTypedValue;
  readonly targetSourceRecordSha256: string;
}

export interface CandidatePropertyValidationSession {
  readonly sourceSha256: string;
  authorReplacement(
    target: CandidatePropertyTargetAssertions,
    before: CandidateTypedValue,
    after: CandidateTypedValue,
    order: number
  ): Promise<CandidatePropertyProposalOperation>;
}

export interface D2PropertyExpectedResultCandidate {
  readonly contractVersion: typeof D2_PROPERTY_EXPECTED_RESULT_CANDIDATE_VERSION;
  readonly status: "private-test-only-expected-result-candidate";
  readonly authority: "none";
  readonly localValidation: { readonly state: "partial"; readonly authoritativeVerdict: false };
  readonly digests: {
    readonly sourceSha256: string;
    readonly proposalSha256: string;
    readonly outputSha256: string;
  };
  readonly output: { readonly filename: string; readonly byteLength: number };
  readonly outcomes: {
    readonly requested: 1;
    readonly applied: 1;
    readonly refused: 0;
    readonly unsupported: 0;
    readonly exactClosure: true;
    readonly touchedExpressRecords: readonly [number];
  };
  readonly verification: {
    readonly parsedAndReimported: true;
    readonly requestedChangeFoundExactlyOnce: 1;
    readonly occurrenceIdentityPreserved: true;
    readonly unknownPlanKeysPreserved: readonly string[];
    readonly untouchedSourceRecordsPreserved: true;
    readonly derivedAuditExpressRecordsChanged: readonly [number];
    readonly allChangedExpressRecords: readonly [number, number];
    readonly sourceRecordCount: number;
    readonly outputRecordCount: number;
  };
}

interface ExpressRecord {
  readonly expressId: number;
  readonly entity: string;
  readonly text: string;
  readonly body: string;
}

interface ParsedPropertyValue {
  readonly name: string;
  readonly value: CandidateTypedValue;
}

const RECORD_LINE = /^#([1-9][0-9]*)=([A-Z][A-Z0-9_]*)\((.*)\);$/;
const PROPERTY_VALUE = /^'((?:''|[^'])*)',\$,([A-Z][A-Z0-9_]*)\((.*)\),\$$/;

function decodeIfcString(value: string): string {
  return value.replaceAll("''", "'");
}

function encodeCanonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(encodeCanonical);
  if (value !== null && typeof value === "object") {
    const source = value as Readonly<Record<string, unknown>>;
    return Object.fromEntries(Object.keys(source).sort().map((key) => {
      const entry = source[key];
      if (entry === undefined) throw new Error(`Canonical JSON cannot encode undefined at ${key}.`);
      return [key, encodeCanonical(entry)];
    }));
  }
  if (typeof value === "number" && !Number.isFinite(value)) throw new Error("Canonical JSON cannot encode a non-finite number.");
  if (["string", "number", "boolean"].includes(typeof value) || value === null) return value;
  throw new Error(`Canonical JSON cannot encode ${typeof value}.`);
}

export function canonicalJson(value: unknown): string {
  return JSON.stringify(encodeCanonical(value));
}

export function canonicalJsonBytes(value: unknown): Uint8Array {
  return new TextEncoder().encode(canonicalJson(value));
}

export function assertD2PropertyCandidateSelectionCount(count: number): void {
  if (!Number.isSafeInteger(count) || count < 0) throw new Error("Candidate selection count must be a non-negative safe integer.");
  if (count === 0) throw new Error("Candidate selection is empty.");
  if (count > D2_PROPERTY_CANDIDATE_CAP) throw new Error(`Candidate selection exceeds the test-only cap of ${D2_PROPERTY_CANDIDATE_CAP}.`);
}

function indexSelfAuthoredCandidateRecords(bytes: Uint8Array): ReadonlyMap<number, ExpressRecord> {
  const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  if (!text.startsWith("ISO-10303-21;")) throw new Error("Candidate source is not an IFC-SPF exchange file.");
  const records = new Map<number, ExpressRecord>();
  for (const line of text.split(/\r?\n/)) {
    if (!line.startsWith("#")) continue;
    const match = RECORD_LINE.exec(line);
    if (match === null) throw new Error("Candidate verifier only accepts one complete EXPRESS record per line.");
    const expressId = Number(match[1]);
    if (records.has(expressId)) throw new Error(`Duplicate EXPRESS locator #${expressId}.`);
    records.set(expressId, { expressId, entity: match[2], body: match[3], text: line });
  }
  if (records.size === 0) throw new Error("Candidate source contains no EXPRESS records.");
  return records;
}

async function recordAssertion(record: ExpressRecord): Promise<CandidateRecordAssertion> {
  return { entity: record.entity, expressId: record.expressId, sourceRecordSha256: await sha256Hex(new TextEncoder().encode(record.text)) };
}

function stringArguments(body: string): readonly string[] {
  const values: string[] = [];
  for (const match of body.matchAll(/'((?:''|[^'])*)'/g)) values.push(decodeIfcString(match[1]));
  return values;
}

function propertySetReferences(record: ExpressRecord): readonly number[] {
  const list = /,\((#[0-9]+(?:,#[0-9]+)*)\)$/.exec(record.body);
  if (list === null) throw new Error(`#${record.expressId} is not a supported self-authored IfcPropertySet record.`);
  return list[1].split(",").map((reference) => Number(reference.slice(1)));
}

function relationLinks(record: ExpressRecord, occurrenceExpressId: number, propertySetExpressId: number): boolean {
  return record.entity === "IFCRELDEFINESBYPROPERTIES" &&
    record.body.endsWith(`,(#${occurrenceExpressId}),#${propertySetExpressId}`);
}

function parsePrimitive(ifcType: string, encoded: string): CandidateTypedValue {
  const stringMatch = /^'((?:''|[^'])*)'$/.exec(encoded);
  if (stringMatch !== null) return { primitiveType: "string", ifcType, value: decodeIfcString(stringMatch[1]) };
  if (encoded === ".T." || encoded === ".F.") return { primitiveType: "boolean", ifcType, value: encoded === ".T." };
  const number = Number(encoded);
  if (encoded !== "" && Number.isFinite(number)) return { primitiveType: "number", ifcType, value: number };
  throw new Error(`Unsupported candidate primitive encoding for ${ifcType}.`);
}

function parsePropertyValue(record: ExpressRecord): ParsedPropertyValue {
  if (record.entity !== "IFCPROPERTYSINGLEVALUE") throw new Error(`#${record.expressId} is not IFCPROPERTYSINGLEVALUE.`);
  const match = PROPERTY_VALUE.exec(record.body);
  if (match === null) throw new Error(`#${record.expressId} is not a supported self-authored single value.`);
  return { name: decodeIfcString(match[1]), value: parsePrimitive(match[2], match[3]) };
}

function sameTypedValue(left: CandidateTypedValue, right: CandidateTypedValue): boolean {
  return left.primitiveType === right.primitiveType && left.ifcType === right.ifcType && Object.is(left.value, right.value);
}

function copyTargetAssertions(target: CandidatePropertyTargetAssertions): CandidatePropertyTargetAssertions {
  return {
    occurrence: {
      entity: target.occurrence.entity,
      expressId: target.occurrence.expressId,
      sourceRecordSha256: target.occurrence.sourceRecordSha256,
      globalId: target.occurrence.globalId,
      name: target.occurrence.name
    },
    propertySet: {
      entity: target.propertySet.entity,
      expressId: target.propertySet.expressId,
      sourceRecordSha256: target.propertySet.sourceRecordSha256,
      name: target.propertySet.name
    },
    property: {
      entity: target.property.entity,
      expressId: target.property.expressId,
      sourceRecordSha256: target.property.sourceRecordSha256,
      name: target.property.name
    },
    relation: {
      entity: target.relation.entity,
      expressId: target.relation.expressId,
      sourceRecordSha256: target.relation.sourceRecordSha256
    }
  };
}

function requireRecord(records: ReadonlyMap<number, ExpressRecord>, assertion: CandidateRecordAssertion): ExpressRecord {
  const record = records.get(assertion.expressId);
  if (record === undefined) throw new Error(`EXPRESS locator #${assertion.expressId} is absent.`);
  if (record.entity !== assertion.entity) throw new Error(`EXPRESS locator #${assertion.expressId} entity mismatch.`);
  return record;
}

async function requireRecordDigest(record: ExpressRecord, assertion: CandidateRecordAssertion): Promise<void> {
  const digest = await sha256Hex(new TextEncoder().encode(record.text));
  if (digest !== assertion.sourceRecordSha256) throw new Error(`EXPRESS locator #${record.expressId} record digest mismatch.`);
}

function findNamedRecord(records: ReadonlyMap<number, ExpressRecord>, entity: string, name: string, nameArgumentIndex: number): ExpressRecord {
  const matches = [...records.values()].filter((record) => record.entity === entity && stringArguments(record.body)[nameArgumentIndex] === name);
  if (matches.length !== 1) throw new Error(`${entity} named ${name} is ambiguous or absent (${matches.length} matches).`);
  return matches[0];
}

export async function discoverSelfAuthoredPropertyTarget(
  sourceBytes: Uint8Array,
  identity: { readonly occurrenceEntity: string; readonly occurrenceName: string; readonly propertySetName: string; readonly propertyName: string }
): Promise<DiscoveredCandidatePropertyTarget> {
  const records = indexSelfAuthoredCandidateRecords(sourceBytes);
  const occurrence = findNamedRecord(records, identity.occurrenceEntity, identity.occurrenceName, 1);
  const occurrenceStrings = stringArguments(occurrence.body);
  const propertySet = findNamedRecord(records, "IFCPROPERTYSET", identity.propertySetName, 1);
  const propertyReferences = propertySetReferences(propertySet);
  const propertyMatches = propertyReferences
    .map((expressId) => records.get(expressId))
    .filter((record): record is ExpressRecord => record !== undefined && record.entity === "IFCPROPERTYSINGLEVALUE")
    .filter((record) => parsePropertyValue(record).name === identity.propertyName);
  if (propertyMatches.length !== 1) {
    throw new Error(`${identity.propertySetName}.${identity.propertyName} is ambiguous or absent (${propertyMatches.length} matches).`);
  }
  const property = propertyMatches[0];
  const relations = [...records.values()].filter((record) => relationLinks(record, occurrence.expressId, propertySet.expressId));
  if (relations.length !== 1) throw new Error("Occurrence-to-property-set relation is ambiguous or absent.");
  const relation = relations[0];
  const parsedProperty = parsePropertyValue(property);
  return {
    occurrence: { ...await recordAssertion(occurrence), globalId: occurrenceStrings[0], name: identity.occurrenceName },
    propertySet: { ...await recordAssertion(propertySet), name: identity.propertySetName },
    property: { ...await recordAssertion(property), name: identity.propertyName },
    relation: await recordAssertion(relation),
    before: parsedProperty.value
  };
}

async function validateTargetAssertions(
  records: ReadonlyMap<number, ExpressRecord>,
  target: CandidatePropertyTargetAssertions,
  expected: CandidateTypedValue,
  verifySourceDigests: boolean
): Promise<void> {
  const occurrence = requireRecord(records, target.occurrence);
  const propertySet = requireRecord(records, target.propertySet);
  const property = requireRecord(records, target.property);
  const relation = requireRecord(records, target.relation);
  if (verifySourceDigests) await Promise.all([
    requireRecordDigest(occurrence, target.occurrence),
    requireRecordDigest(propertySet, target.propertySet),
    requireRecordDigest(property, target.property),
    requireRecordDigest(relation, target.relation)
  ]);
  const occurrenceStrings = stringArguments(occurrence.body);
  if (occurrenceStrings[0] !== target.occurrence.globalId || occurrenceStrings[1] !== target.occurrence.name) {
    throw new Error("Occurrence GlobalId or name assertion mismatch.");
  }
  if (stringArguments(propertySet.body)[1] !== target.propertySet.name) throw new Error("Property-set name assertion mismatch.");
  const linkedProperties = propertySetReferences(propertySet)
    .map((expressId) => records.get(expressId))
    .filter((record): record is ExpressRecord => record !== undefined && record.entity === "IFCPROPERTYSINGLEVALUE")
    .filter((record) => parsePropertyValue(record).name === target.property.name);
  if (linkedProperties.length !== 1 || linkedProperties[0].expressId !== property.expressId) {
    throw new Error(`Same-name property ambiguity for ${target.propertySet.name}.${target.property.name}.`);
  }
  if (!relationLinks(relation, occurrence.expressId, propertySet.expressId)) throw new Error("Relation locator does not link the asserted occurrence and property set.");
  const parsed = parsePropertyValue(property);
  if (parsed.name !== target.property.name || !sameTypedValue(parsed.value, expected)) throw new Error("Property type or before-value assertion mismatch.");
}

export async function createCandidatePropertyValidationSession(
  sourceBytes: Uint8Array,
  claimedSourceSha256: string
): Promise<CandidatePropertyValidationSession> {
  const sourceSha256 = await sha256Hex(sourceBytes);
  if (sourceSha256 !== claimedSourceSha256) throw new Error("Candidate source digest mismatch.");
  const records = indexSelfAuthoredCandidateRecords(sourceBytes);
  return {
    sourceSha256,
    async authorReplacement(target, before, after, order) {
      if (!Number.isSafeInteger(order) || order < 0) throw new Error("Candidate operation order must be a non-negative safe integer.");
      if (before.primitiveType !== after.primitiveType || before.ifcType !== after.ifcType) {
        throw new Error("Candidate replacement must preserve primitive and IFC value types.");
      }
      if (Object.is(before.value, after.value)) throw new Error("Candidate replacement is already satisfied.");
      await validateTargetAssertions(records, target, before, true);
      const opSeed = canonicalJsonBytes({
        sourceSha256,
        occurrence: target.occurrence.expressId,
        propertySet: target.propertySet.expressId,
        property: target.property.expressId,
        after
      });
      return {
        opId: `op-sha256-${await sha256Hex(opSeed)}`,
        order,
        kind: "replace-existing-occurrence-property-single-value",
        target: copyTargetAssertions(target),
        before: structuredClone(before),
        after: structuredClone(after),
        targetSourceRecordSha256: target.property.sourceRecordSha256
      };
    }
  };
}

export async function authorD2PropertyProposalCandidate(input: D2PropertyCandidateAuthoringInput): Promise<D2PropertyProposalCandidate> {
  const selectedCount = input.target === undefined ? 0 : 1;
  assertD2PropertyCandidateSelectionCount(selectedCount);
  const session = await createCandidatePropertyValidationSession(input.sourceBytes, input.claimedSourceSha256);
  const authored = await session.authorReplacement(input.target, input.before, input.after, 0);
  const operation: D2PropertyProposalOperation = { ...authored, order: 0 };
  return {
    contractVersion: D2_PROPERTY_PROPOSAL_CANDIDATE_VERSION,
    status: "private-test-only-candidate",
    authority: "none",
    canonicalBytes: {
      mediaType: "application/json",
      encoding: "UTF-8",
      objectKeys: "lexicographic",
      arrays: "preserve-contract-order",
      insignificantWhitespace: "none",
      bom: false,
      trailingNewline: false
    },
    source: {
      filename: input.sourceFilename,
      byteLength: input.sourceBytes.byteLength,
      sha256: session.sourceSha256,
      authorityClaim: false
    },
    validation: { state: "partial", serverVerdict: false },
    selectedPopulation: {
      selected: 1,
      proposed: 1,
      alreadySatisfied: 0,
      refused: 0,
      unsupported: 0,
      exactClosure: true,
      candidateCap: D2_PROPERTY_CANDIDATE_CAP,
      candidateCapIsProductionLimit: false
    },
    operations: [operation],
    preservationAssertions: {
      unknownPlanKeys: [...input.preservedUnknownPlanKeys].sort(),
      untouchedSourceRecords: true
    }
  };
}

function sourceJsonPlan(records: ReadonlyMap<number, ExpressRecord>): Readonly<Record<string, unknown>> {
  const sourceJson = [...records.values()].filter((record) => {
    if (record.entity !== "IFCPROPERTYSINGLEVALUE") return false;
    try { return parsePropertyValue(record).name === "SourceJson"; } catch { return false; }
  });
  if (sourceJson.length !== 1) throw new Error(`Expected one SourceJson record, found ${sourceJson.length}.`);
  const match = /^'SourceJson',\$,IFCTEXT\('((?:''|[^'])*)'\),\$$/.exec(sourceJson[0].body);
  if (match === null) throw new Error("SourceJson record has an unsupported encoding.");
  const parsed: unknown = JSON.parse(decodeIfcString(match[1]));
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("SourceJson is not an object Plan.");
  return parsed as Readonly<Record<string, unknown>>;
}

function sourceJsonExpressId(records: ReadonlyMap<number, ExpressRecord>): number {
  const record = [...records.values()].find((candidate) => candidate.entity === "IFCPROPERTYSINGLEVALUE" && candidate.body.startsWith("'SourceJson',$,IFCTEXT("));
  if (record === undefined) throw new Error("SourceJson record is absent.");
  return record.expressId;
}

export async function buildD2PropertyExpectedResultCandidate(input: {
  readonly sourceBytes: Uint8Array;
  readonly outputBytes: Uint8Array;
  readonly outputFilename: string;
  readonly proposal: D2PropertyProposalCandidate;
  readonly parsedAndReimported: boolean;
  readonly requestedChangeFoundExactlyOnce: number;
}): Promise<D2PropertyExpectedResultCandidate> {
  const sourceSha256 = await sha256Hex(input.sourceBytes);
  if (sourceSha256 !== input.proposal.source.sha256) throw new Error("Expected-result source digest does not match the proposal.");
  if (!input.parsedAndReimported) throw new Error("Expected output was not parsed and reimported.");
  if (input.requestedChangeFoundExactlyOnce !== 1) throw new Error("Expected output does not contain the requested change exactly once.");
  const sourceRecords = indexSelfAuthoredCandidateRecords(input.sourceBytes);
  const outputRecords = indexSelfAuthoredCandidateRecords(input.outputBytes);
  const operation = input.proposal.operations[0];
  await validateTargetAssertions(outputRecords, operation.target, operation.after, false);
  const outputOccurrence = requireRecord(outputRecords, operation.target.occurrence);
  if (stringArguments(outputOccurrence.body)[0] !== operation.target.occurrence.globalId) throw new Error("Output occurrence GlobalId changed.");

  const sourcePlan = sourceJsonPlan(sourceRecords);
  const outputPlan = sourceJsonPlan(outputRecords);
  const preservedUnknownPlanKeys = input.proposal.preservationAssertions.unknownPlanKeys.filter((key) =>
    Object.hasOwn(sourcePlan, key) && Object.hasOwn(outputPlan, key) && canonicalJson(sourcePlan[key]) === canonicalJson(outputPlan[key])
  );
  if (preservedUnknownPlanKeys.length !== input.proposal.preservationAssertions.unknownPlanKeys.length) {
    throw new Error("An asserted unknown Plan key was not preserved.");
  }

  const sourceJsonRecord = sourceJsonExpressId(sourceRecords);
  const excluded = new Set([operation.target.property.expressId, sourceJsonRecord]);
  const changedExpressRecords = [...sourceRecords]
    .filter(([expressId, sourceRecord]) => outputRecords.get(expressId)?.text !== sourceRecord.text)
    .map(([expressId]) => expressId)
    .sort((left, right) => left - right);
  const expectedChangedRecords = [sourceJsonRecord, operation.target.property.expressId].sort((left, right) => left - right);
  for (const [expressId, sourceRecord] of sourceRecords) {
    if (excluded.has(expressId)) continue;
    if (outputRecords.get(expressId)?.text !== sourceRecord.text) throw new Error(`Untouched EXPRESS record #${expressId} changed.`);
  }
  if (canonicalJson(changedExpressRecords) !== canonicalJson(expectedChangedRecords)) {
    throw new Error(`Expected exactly the target property and derived SourceJson records to change; found ${changedExpressRecords.join(", ")}.`);
  }
  if (sourceRecords.size !== outputRecords.size) throw new Error("Output EXPRESS record count changed.");

  const proposalSha256 = await sha256Hex(canonicalJsonBytes(input.proposal));
  const outputSha256 = await sha256Hex(input.outputBytes);
  return {
    contractVersion: D2_PROPERTY_EXPECTED_RESULT_CANDIDATE_VERSION,
    status: "private-test-only-expected-result-candidate",
    authority: "none",
    localValidation: { state: "partial", authoritativeVerdict: false },
    digests: { sourceSha256, proposalSha256, outputSha256 },
    output: { filename: input.outputFilename, byteLength: input.outputBytes.byteLength },
    outcomes: {
      requested: 1,
      applied: 1,
      refused: 0,
      unsupported: 0,
      exactClosure: true,
      touchedExpressRecords: [operation.target.property.expressId]
    },
    verification: {
      parsedAndReimported: true,
      requestedChangeFoundExactlyOnce: 1,
      occurrenceIdentityPreserved: true,
      unknownPlanKeysPreserved: preservedUnknownPlanKeys,
      untouchedSourceRecordsPreserved: true,
      derivedAuditExpressRecordsChanged: [sourceJsonRecord],
      allChangedExpressRecords: expectedChangedRecords as [number, number],
      sourceRecordCount: sourceRecords.size,
      outputRecordCount: outputRecords.size
    }
  };
}
